import {Injectable} from '@angular/core';
import {RestApiService} from './rest-api.service';
import {Subject} from 'rxjs';
import {ActualAppointmentReq, ActualAppointmentRes} from '../contracts/v0/resource-manager';
import {BaseTaskRes, ProjectTrackerRes} from '../contracts/v0/crm';
import {
    CalendarEntryCreateBatchReq,
    CalendarEntryCreateTypedReq
} from '../contracts/v0/resource-manager/calendar.contracts';
import {RuntimeCacheService} from './runtime-cache.service';
import {RmCalendarEntryType} from '../enums/calendar-enums';

export class TimerSection {
    start?: Date
    end?: Date

    constructor(time: Partial<TimerSection>) {
        this.start = time.start
        this.end = time.end
    }

    get duration(): Date {
        return new Date(this.end!.getTime() - this.start!.getTime());
    }

    public toAppointment(task: BaseTaskRes, color?: string) {
        return new ActualAppointmentReq(timeToDate(this.start!), timeToDate(this.end!), task.description ?? '', color ?? '', task)
    }
}

function timeToDate(time: Date): Date {
    return new Date(new Date().setHours(time.getHours(), time.getMinutes(), time.getSeconds()))
}

@Injectable({
    providedIn: 'root'
})
export class TimeClockService {
    selectedTasks: BaseTaskRes | null = null;
    public currentTimerStart: Date | null = null;
    sections: TimerSection[] = [];
    public ticks: Subject<number> = new Subject();
    public reset: Subject<void> = new Subject();
    public onSave: Subject<ActualAppointmentRes[]> = new Subject();
    private timer: any | null = null;
    public isTimerRunning: boolean = false;
    public selectedProject: ProjectTrackerRes | null = null;
    public selectedTaskId: string | null = null;

    public get currentTime(): Date {
        if (this.currentTimerStart === null) {
            return new Date(0)
        }
        return new Date(Date.now() - this.currentTimerStart!.getTime());
    }

    constructor(
        public rest: RestApiService,
        public runtime: RuntimeCacheService) {
    }

    public startTimer() {
        this.currentTimerStart = new Date(Date.now())
        this.timer = setInterval(() => {
            this.ticks.next(this.onTick())
        }, 100);
        this.isTimerRunning = true;
    }

    public pauseTimer() {
        this.addSection()
    }

    public stopTimer() {
        clearInterval(this.timer)
        this.isTimerRunning = false;
        this.currentTimerStart = null;
        this.reset.next()
        this.sections = []
    }

    public save() {
        if (this.isUnder15Mis()) return
        if (this.isTimerRunning)
            this.addSection()
        let batch = new CalendarEntryCreateBatchReq();
        this.sections.forEach(section => {
            let calendarEntry = new CalendarEntryCreateTypedReq({
                ownerUserId: this.runtime.loginInfo!.id,
                type: RmCalendarEntryType.WorkTime,
                start: section.start,
                end: section.end,
                projectId: this.selectedProject?.id,
                taskId: this.selectedTaskId!,
            })
            batch.items.push(calendarEntry)
        })
        this.rest.v0.resourceManager.calendar.createBatch(batch).subscribe(() => {
            this.sections = []
            clearInterval(this.timer)
        })

    }

    private addSection() {
        this.sections.push(new TimerSection({start: this.currentTimerStart!, end: new Date(Date.now())}))
        clearInterval(this.timer);
        this.currentTimerStart = null;
        this.isTimerRunning = false;
    }

    onTick: () => number = () => {
        let currentTimerLength = Date.now() - this.currentTimerStart!.getTime();
        if (this.sections.length > 0)
            currentTimerLength += this.sections
                .map(x => x.duration.getTime())
                .reduce((a, b) => a + b, 0);
        return currentTimerLength;
    }

    removeSection(index: number) {
        this.sections.splice(index, 1);
        this.ticks.next(this.onTick());
    }

    isUnder15Mis(): boolean {
        let sumDuration = new Date(this.sections.reduce((sum, currentValue) => sum + currentValue.duration.getTime(), 0));

        let h = sumDuration.getUTCHours() * 60;
        let m = sumDuration.getMinutes();
        return (h + m) < 15
    }
}